import { Base } from '../../abstract/base.model';
import { FaaReportOptions } from './faa-report-options.model';
import { FaaReportStatus } from './faa-report-status.model';
import { isRecord } from '../utils/is-record';
import { toFaaReportOptions } from '../utils/faa-report-utils';
import { countOrZero, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * An FAA compliance report, built from the hub's FAA report JSON (`user-faa-report-controller.ts`,
 * hub `data-models/faa-report.ts`). `downloadUrl` is a pre-signed URL, only sent when fetching one report.
 *
 * Read-modify-write rule: the writable fields (`name`, `options`) stay `undefined` when the hub didn't send
 * them, and an explicit `null` stays `null`. Display fallbacks belong in templates.
 */
export class FaaReport extends Base {
  // Writable fields (hub FAA_REPORT_WRITABLE)
  name?: string | null;
  options?: FaaReportOptions | null;

  // Read-only fields
  organizationId?: string | null;
  reportType?: string | null;
  /** Missing status reads as 'draft', the hub's initial state. */
  status: FaaReportStatus;
  startDate?: string | null;
  endDate?: string | null;
  reportUrl?: string | null;
  downloadUrl?: string | null;
  generationProgress: number;
  recordCount?: number | null;
  fileSize?: string | null;
  submittedAt?: string | null;
  submittedTo?: string | null;
  errorMessage?: string | null;
  createdBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.name = nullableString(source['name']);
    this.options = toFaaReportOptions(source['options']);
    this.organizationId = nullableString(source['organizationId']);
    this.reportType = nullableString(source['reportType']);
    this.status = (nullableString(source['status']) ?? 'draft') as FaaReportStatus;
    this.startDate = nullableString(source['startDate']);
    this.endDate = nullableString(source['endDate']);
    this.reportUrl = nullableString(source['reportUrl']);
    this.downloadUrl = nullableString(source['downloadUrl']);
    this.generationProgress = countOrZero(source['generationProgress']);
    this.recordCount = nullableNumber(source['recordCount']);
    this.fileSize = nullableString(source['fileSize']);
    this.submittedAt = nullableString(source['submittedAt']);
    this.submittedTo = nullableString(source['submittedTo']);
    this.errorMessage = nullableString(source['errorMessage']);
    this.createdBy = nullableString(source['createdBy']);
  }
}
